import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Table,
} from "reactstrap";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { usePocketBase } from "../../PocketBaseContext";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

const PersonelManagement = () => {
  document.title = "Personel | Reform Panel";

  const pb = usePocketBase();
  const [personel, setPersonel] = useState([]);
  const [filteredPersonel, setFilteredPersonel] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentPersonel, setCurrentPersonel] = useState(null);
  const [alert, setAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchPersonel = async () => {
    try {
      const records = await pb.collection("personel").getFullList(200, {
        sort: "-created",
      });
      setPersonel(records);
      setFilteredPersonel(records);
    } catch (error) {
      console.error("Personel getirilirken hata oluştu:", error);
      setAlert({
        color: "danger",
        message: "Personel getirilirken bir hata oluştu.",
      });
    }
  };

  useEffect(() => {
    fetchPersonel();
  }, []);

  useEffect(() => {
    const results = personel.filter(
      (person) =>
        person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        person.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        person.position.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPersonel(results);
  }, [searchTerm, personel]);

  const toggleEditModal = () => {
    setEditModal(!editModal);
  };

  const toggleAddModal = () => {
    setAddModal(!addModal);
    if (!addModal) {
      addValidation.resetForm();
    }
  };

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
  };

  const handleEdit = (person) => {
    setCurrentPersonel(person);
    editValidation.setValues({
      name: person.name,
      surname: person.surname,
      position: person.position,
      department: person.department,
      email: person.email,
      cellphone: person.cellphone,
      hire_date: person.hire_date,
      adress: person.adress,
      vergi_numarasi: person.vergi_numarasi,
      vergi_dairesi: person.vergi_dairesi,
      phone: person.phone,
    });
    toggleEditModal();
  };

  const handleDeleteClick = (person) => {
    setCurrentPersonel(person);
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    try {
      await pb.collection("personel").delete(currentPersonel.id);
      await fetchPersonel();
      setAlert({ color: "success", message: "Personel başarıyla silindi." });
      toggleDeleteModal();
    } catch (error) {
      console.error("Personel silinirken hata oluştu:", error);
      setAlert({
        color: "danger",
        message: "Personel silinirken bir hata oluştu.",
      });
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("İsim gereklidir"),
    surname: Yup.string().required("Soyisim gereklidir"),
    position: Yup.string().required("Pozisyon gereklidir"),
    department: Yup.string().required("Departman gereklidir"),
    email: Yup.string()
      .email("Geçerli bir e-posta adresi giriniz")
      .required("E-posta gereklidir"),
    cellphone: Yup.string().required("Cep telefonu gereklidir"),
    hire_date: Yup.date().required("İşe alım tarihi gereklidir"),
    adress: Yup.string().required("Adres gereklidir"),
    vergi_numarasi: Yup.string().required("Vergi numarası gereklidir"),
    vergi_dairesi: Yup.string().required("Vergi dairesi gereklidir"),
    phone: Yup.string().required("Telefon gereklidir"),
  });

  const editValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      surname: "",
      position: "",
      department: "",
      email: "",
      cellphone: "",
      hire_date: "",
      adress: "",
      vergi_numarasi: "",
      vergi_dairesi: "",
      phone: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        await pb.collection("personel").update(currentPersonel.id, values);
        setAlert({
          color: "success",
          message: "Personel başarıyla güncellendi.",
        });
        toggleEditModal();
        await fetchPersonel();
      } catch (error) {
        console.error("Personel güncellenirken hata oluştu:", error);
        setAlert({
          color: "danger",
          message: "Personel güncellenirken bir hata oluştu: " + error.message,
        });
      }
    },
  });

  const addValidation = useFormik({
    initialValues: {
      name: "",
      surname: "",
      position: "",
      department: "",
      email: "",
      cellphone: "",
      hire_date: "",
      adress: "",
      vergi_numarasi: "",
      vergi_dairesi: "",
      phone: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        await pb.collection("personel").create(values);
        setAlert({ color: "success", message: "Personel başarıyla eklendi." });
        toggleAddModal();
        await fetchPersonel();
        addValidation.resetForm();
      } catch (error) {
        console.error("Personel eklenirken hata oluştu:", error);
        setAlert({
          color: "danger",
          message: "Personel eklenirken bir hata oluştu: " + error.message,
        });
      }
    },
  });

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Personel Yönetimi" pageTitle="Personel" />

          {alert && (
            <Alert color={alert.color} className="material-shadow">
              <strong>{alert.message}</strong>
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">Personel</h4>
                  <Button color="primary" onClick={toggleAddModal}>
                    Yeni Personel Ekle
                  </Button>
                </CardHeader>

                <CardBody>
                  <Container fluid>
                    <Row className="mb-3">
                      <Col xs="12" sm="6" md="4">
                        <Input
                          type="search"
                          className="form-control"
                          placeholder="Personel ara..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </Col>
                    </Row>
                  </Container>
                </CardBody>

                <CardBody>
  <div className="table-responsive">
    <Table className="align-middle table-nowrap mb-0">
      <thead className="table-light">
        <tr>
          <th scope="col" style={{ width: "42px" }}>
            <div className="form-check">
              <Input
                className="form-check-input"
                type="checkbox"
                defaultValue=""
                id="responsivetableCheck"
              />
              <Label
                className="form-check-label"
                for="responsivetableCheck"
              ></Label>
            </div>
          </th>
          <th scope="col">Ad</th>
          <th scope="col">Soyad</th>
          <th scope="col">Pozisyon</th>
          <th scope="col">Departman</th>
          <th scope="col">E-posta</th>
          <th scope="col">Cep Telefonu</th>
          <th scope="col" style={{ maxWidth: "200px" }}>Adres</th>
          <th scope="col">Vergi Numarası</th>
          <th scope="col">Vergi Dairesi</th>
          <th scope="col">Telefon</th>
          <th scope="col">Oluşturulma Tarihi</th>
          <th scope="col">Güncellenme Tarihi</th>
          <th scope="col">İşlemler</th>
        </tr>
      </thead>
      <tbody>
        {filteredPersonel.map((person) => (
          <tr 
            key={person.id} 
            onClick={() => handleEdit(person)} 
            style={{ cursor: 'pointer' }}
          >
            <th scope="row" onClick={(e) => e.stopPropagation()}>
              <div className="form-check">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  defaultValue=""
                  id={`responsivetableCheck${person.id}`}
                />
                <Label
                  className="form-check-label"
                  htmlFor={`responsivetableCheck${person.id}`}
                ></Label>
              </div>
            </th>
            <td>{person.name}</td>
            <td>{person.surname}</td>
            <td>{person.position}</td>
            <td>{person.department}</td>
            <td>{person.email}</td>
            <td>{person.cellphone}</td>
            <td style={{ maxWidth: "200px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={person.adress}>
              {person.adress}
            </td>
            <td>{person.vergi_numarasi}</td>
            <td>{person.vergi_dairesi}</td>
            <td>{person.phone}</td>
            <td>{new Date(person.created).toLocaleString()}</td>
            <td>{new Date(person.updated).toLocaleString()}</td>
            <td onClick={(e) => e.stopPropagation()}>
              <div className="hstack gap-3 fs-15">
                <Link
                  to="#"
                  className="link-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(person);
                  }}
                >
                  <i className="ri-edit-2-line"></i>
                </Link>
                <Link
                  to="#"
                  className="link-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(person);
                  }}
                >
                  <i className="ri-delete-bin-5-line"></i>
                </Link>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
</CardBody>
                
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Düzenleme Modal */}
      <Modal isOpen={editModal} toggle={toggleEditModal} size="lg">
        <ModalHeader toggle={toggleEditModal}>Personel Düzenle</ModalHeader>
        <ModalBody>
          <Form onSubmit={editValidation.handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="name">Ad</Label>
                  <Input
                    type="text"
                    name="name"
                    id="name"
                    {...editValidation.getFieldProps("name")}
                    invalid={
                      editValidation.touched.name && editValidation.errors.name
                    }
                  />
                  {editValidation.touched.name &&
                    editValidation.errors.name && (
                      <div className="text-danger">
                        {editValidation.errors.name}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="surname">Soyad</Label>
                  <Input
                    type="text"
                    name="surname"
                    id="surname"
                    {...editValidation.getFieldProps("surname")}
                    invalid={
                      editValidation.touched.surname &&
                      editValidation.errors.surname
                    }
                  />
                  {editValidation.touched.surname &&
                    editValidation.errors.surname && (
                      <div className="text-danger">
                        {editValidation.errors.surname}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="position">Pozisyon</Label>
                  <Input
                    type="text"
                    name="position"
                    id="position"
                    {...editValidation.getFieldProps("position")}
                    invalid={
                      editValidation.touched.position &&
                      editValidation.errors.position
                    }
                  />
                  {editValidation.touched.position &&
                    editValidation.errors.position && (
                      <div className="text-danger">
                        {editValidation.errors.position}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="department">Departman</Label>
                  <Input
                    type="text"
                    name="department"
                    id="department"
                    {...editValidation.getFieldProps("department")}
                    invalid={
                      editValidation.touched.department &&
                      editValidation.errors.department
                    }
                  />
                  {editValidation.touched.department &&
                    editValidation.errors.department && (
                      <div className="text-danger">
                        {editValidation.errors.department}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="email">E-posta</Label>
                  <Input
                    type="email"
                    name="email"
                    id="email"
                    {...editValidation.getFieldProps("email")}
                    invalid={
                      editValidation.touched.email &&
                      editValidation.errors.email
                    }
                  />
                  {editValidation.touched.email &&
                    editValidation.errors.email && (
                      <div className="text-danger">
                        {editValidation.errors.email}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="cellphone">Cep Telefonu</Label>
                  <Input
                    type="text"
                    name="cellphone"
                    id="cellphone"
                    {...editValidation.getFieldProps("cellphone")}
                    invalid={
                      editValidation.touched.cellphone &&
                      editValidation.errors.cellphone
                    }
                  />
                  {editValidation.touched.cellphone &&
                    editValidation.errors.cellphone && (
                      <div className="text-danger">
                        {editValidation.errors.cellphone}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="hire_date">İşe Alım Tarihi</Label>
                  <Flatpickr
                    className="form-control"
                    options={{
                      dateFormat: "Y-m-d",
                    }}
                    {...editValidation.getFieldProps("hire_date")}
                    onChange={(date) => {
                      editValidation.setFieldValue(
                        "hire_date",
                        date[0].toISOString()
                      );
                    }}
                  />
                  {editValidation.touched.hire_date &&
                    editValidation.errors.hire_date && (
                      <div className="text-danger">
                        {editValidation.errors.hire_date}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="phone">Telefon</Label>
                  <Input
                    type="text"
                    name="phone"
                    id="phone"
                    {...editValidation.getFieldProps("phone")}
                    invalid={
                      editValidation.touched.phone &&
                      editValidation.errors.phone
                    }
                  />
                  {editValidation.touched.phone &&
                    editValidation.errors.phone && (
                      <div className="text-danger">
                        {editValidation.errors.phone}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="adress">Adres</Label>
                  <Input
                    type="textarea"
                    name="adress"
                    id="adress"
                    {...editValidation.getFieldProps("adress")}
                    invalid={
                      editValidation.touched.adress &&
                      editValidation.errors.adress
                    }
                  />
                  {editValidation.touched.adress &&
                    editValidation.errors.adress && (
                      <div className="text-danger">
                        {editValidation.errors.adress}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="vergi_numarasi">Vergi Numarası</Label>
                  <Input
                    type="text"
                    name="vergi_numarasi"
                    id="vergi_numarasi"
                    {...editValidation.getFieldProps("vergi_numarasi")}
                    invalid={
                      editValidation.touched.vergi_numarasi &&
                      editValidation.errors.vergi_numarasi
                    }
                  />
                  {editValidation.touched.vergi_numarasi &&
                    editValidation.errors.vergi_numarasi && (
                      <div className="text-danger">
                        {editValidation.errors.vergi_numarasi}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="vergi_dairesi">Vergi Dairesi</Label>
                  <Input
                    type="text"
                    name="vergi_dairesi"
                    id="vergi_dairesi"
                    {...editValidation.getFieldProps("vergi_dairesi")}
                    invalid={
                      editValidation.touched.vergi_dairesi &&
                      editValidation.errors.vergi_dairesi
                    }
                  />
                  {editValidation.touched.vergi_dairesi &&
                    editValidation.errors.vergi_dairesi && (
                      <div className="text-danger">
                        {editValidation.errors.vergi_dairesi}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Button color="primary" type="submit">
              Güncelle
            </Button>
          </Form>
        </ModalBody>
      </Modal>

      {/* Ekleme Modal */}
      <Modal isOpen={addModal} toggle={toggleAddModal} size="lg">
        <ModalHeader toggle={toggleAddModal}>Yeni Personel Ekle</ModalHeader>
        <ModalBody>
          <Form onSubmit={addValidation.handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="name">Ad</Label>
                  <Input
                    type="text"
                    name="name"
                    id="name"
                    {...addValidation.getFieldProps("name")}
                    invalid={
                      addValidation.touched.name && addValidation.errors.name
                    }
                  />
                  {addValidation.touched.name && addValidation.errors.name && (
                    <div className="text-danger">
                      {addValidation.errors.name}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="surname">Soyad</Label>
                  <Input
                    type="text"
                    name="surname"
                    id="surname"
                    {...addValidation.getFieldProps("surname")}
                    invalid={
                      addValidation.touched.surname &&
                      addValidation.errors.surname
                    }
                  />
                  {addValidation.touched.surname &&
                    addValidation.errors.surname && (
                      <div className="text-danger">
                        {addValidation.errors.surname}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="position">Pozisyon</Label>
                  <Input
                    type="text"
                    name="position"
                    id="position"
                    {...addValidation.getFieldProps("position")}
                    invalid={
                      addValidation.touched.position &&
                      addValidation.errors.position
                    }
                  />
                  {addValidation.touched.position &&
                    addValidation.errors.position && (
                      <div className="text-danger">
                        {addValidation.errors.position}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="department">Departman</Label>
                  <Input
                    type="text"
                    name="department"
                    id="department"
                    {...addValidation.getFieldProps("department")}
                    invalid={
                      addValidation.touched.department &&
                      addValidation.errors.department
                    }
                  />
                  {addValidation.touched.department &&
                    addValidation.errors.department && (
                      <div className="text-danger">
                        {addValidation.errors.department}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="email">E-posta</Label>
                  <Input
                    type="email"
                    name="email"
                    id="email"
                    {...addValidation.getFieldProps("email")}
                    invalid={
                      addValidation.touched.email && addValidation.errors.email
                    }
                  />
                  {addValidation.touched.email &&
                    addValidation.errors.email && (
                      <div className="text-danger">
                        {addValidation.errors.email}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="cellphone">Cep Telefonu</Label>
                  <Input
                    type="text"
                    name="cellphone"
                    id="cellphone"
                    {...addValidation.getFieldProps("cellphone")}
                    invalid={
                      addValidation.touched.cellphone &&
                      addValidation.errors.cellphone
                    }
                  />
                  {addValidation.touched.cellphone &&
                    addValidation.errors.cellphone && (
                      <div className="text-danger">
                        {addValidation.errors.cellphone}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="hire_date">İşe Alım Tarihi</Label>
                  <Flatpickr
                    className="form-control"
                    options={{
                      dateFormat: "Y-m-d",
                    }}
                    {...addValidation.getFieldProps("hire_date")}
                    onChange={(date) => {
                      addValidation.setFieldValue(
                        "hire_date",
                        date[0].toISOString()
                      );
                    }}
                  />
                  {addValidation.touched.hire_date &&
                    addValidation.errors.hire_date && (
                      <div className="text-danger">
                        {addValidation.errors.hire_date}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="phone">Telefon</Label>
                  <Input
                    type="text"
                    name="phone"
                    id="phone"
                    {...addValidation.getFieldProps("phone")}
                    invalid={
                      addValidation.touched.phone && addValidation.errors.phone
                    }
                  />
                  {addValidation.touched.phone &&
                    addValidation.errors.phone && (
                      <div className="text-danger">
                        {addValidation.errors.phone}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="adress">Adres</Label>
                  <Input
                    type="textarea"
                    name="adress"
                    id="adress"
                    {...addValidation.getFieldProps("adress")}
                    invalid={
                      addValidation.touched.adress &&
                      addValidation.errors.adress
                    }
                  />
                  {addValidation.touched.adress &&
                    addValidation.errors.adress && (
                      <div className="text-danger">
                        {addValidation.errors.adress}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="vergi_numarasi">Vergi Numarası</Label>
                  <Input
                    type="text"
                    name="vergi_numarasi"
                    id="vergi_numarasi"
                    {...addValidation.getFieldProps("vergi_numarasi")}
                    invalid={
                      addValidation.touched.vergi_numarasi &&
                      addValidation.errors.vergi_numarasi
                    }
                  />
                  {addValidation.touched.vergi_numarasi &&
                    addValidation.errors.vergi_numarasi && (
                      <div className="text-danger">
                        {addValidation.errors.vergi_numarasi}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="vergi_dairesi">Vergi Dairesi</Label>
                  <Input
                    type="text"
                    name="vergi_dairesi"
                    id="vergi_dairesi"
                    {...addValidation.getFieldProps("vergi_dairesi")}
                    invalid={
                      addValidation.touched.vergi_dairesi &&
                      addValidation.errors.vergi_dairesi
                    }
                  />
                  {addValidation.touched.vergi_dairesi &&
                    addValidation.errors.vergi_dairesi && (
                      <div className="text-danger">
                        {addValidation.errors.vergi_dairesi}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Button color="primary" type="submit">
              Ekle
            </Button>
          </Form>
        </ModalBody>
      </Modal>

      {/* Silme Onay Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
  <ModalHeader toggle={toggleDeleteModal}>Personel Sil</ModalHeader>
  <ModalBody class="p-4">
    <div class="bg-light rounded shadow-sm p-3 mb-4">
      <h4 class="text-danger fw-bold">Kullanıcı Bilgilerini Sil</h4>
      <div class="d-flex justify-content-between">
        <div>
          <p class="mb-1"><strong>Adı:</strong> {currentPersonel?.name}</p>
          <p class="mb-1"><strong>Soyadı:</strong> {currentPersonel?.surname}</p>
        </div>
        <div>
          <i class="fa fa-user-circle fa-3x text-muted"></i>
        </div>
      </div>
    </div>
    <h5 class="text-danger mb-4">Bu işlem geri alınamaz.</h5>
  </ModalBody>

  <ModalFooter className="d-flex justify-content-between">
  <button class="btn btn-danger btn-lg w-100 " onClick={handleDelete}>
      <i class="fa fa-trash me-2"></i> Sil
    </button>
    <button class="btn btn-outline-secondary btn-lg w-100" onClick={toggleDeleteModal}>
      <i class="fa fa-times me-2"></i> İptal
    </button>
   <p> Müşteri veya şirket içi personeller buraya eklenmelidir. Öncelikle personel bilgilerini doğru bir şekilde giriniz. Daha sonra müşteri sayfasında ilgili şirket adına bu kişiyi ekleyiniz.</p>
  </ModalFooter>
</Modal>

    </React.Fragment>
  );
};

export default PersonelManagement;
